/** The member names of {@link SurfaceServices}, in declaration order. A complete fake implements each. */
export const SURFACE_SERVICE_MEMBERS = Object.freeze([
    'fetch',
    'permissions',
    'polarity',
    'branding',
    'preferences',
    'recents',
    'savedState',
    'location',
    'session',
    'settings',
    'portalTarget',
    'extensions',
    'slots',
]);
// ── Service refusals (R-81 a) ─────────────────────────────────────────────────────────────────
/**
 * Every reason a {@link SurfaceServices} member may refuse a call, in a fixed order (R-81 a, WS-8
 * §3.2). The shell's loader services, `FakeSurfaceServices` and the services contract suite read
 * this one list.
 *
 * - `disposed`: the mount ended; every member refuses this after the host released the services.
 * - `root-only`: a setter only the routed root may call (`polarity.set`), and `slots.provide` from
 *   a mount that is not the live root.
 * - `embedded-navigation`: an embedded mount owns no URL and emits `navigate` instead (D5, D14).
 * - `path-invalid`: a navigation target that is not an absolute same-origin path.
 * - `namespace-invalid`: a preference namespace outside the namespace grammar.
 * - `namespace-foreign`: a preference namespace another app owns.
 * - `namespace-reserved`: `home-recents`, whose one writer is the loader's recents controller.
 * - `value-invalid`: a value outside the member's domain, such as a polarity other than the three,
 *   a saved state or preference patch that is not plain JSON data, or a slot id outside the row's
 *   `fills` or `slots`.
 */
export const SURFACE_SERVICE_REFUSAL_REASONS = Object.freeze([
    'disposed',
    'root-only',
    'embedded-navigation',
    'path-invalid',
    'namespace-invalid',
    'namespace-foreign',
    'namespace-reserved',
    'value-invalid',
]);
/**
 * The one error a surface receives when a {@link SurfaceServices} member refuses a call (R-81 a).
 *
 * The host throws it from a sync member and rejects with it from an async member. A surface tells
 * a refusal apart by `name` and `reason`, never by `instanceof` and never by `message`: a host may
 * construct the error from its own copy of this class.
 *
 * - `name` is always `'SurfaceServiceRefusedError'`.
 * - `reason` is the {@link RefusalReason} the caller passed.
 * - `message` is the reason alone when no detail is given, else the reason, a colon, a space and
 *   the detail (`'root-only: polarity.set is the routed root's'`). The message is for people.
 *
 * The detail names the refusing member in words and must never quote the refused value, so no
 * caller data reaches a log through the message. Construction has no side effect and never throws.
 */
export class SurfaceServiceRefusedError extends Error {
    name = 'SurfaceServiceRefusedError';
    reason;
    /**
     * @param reason Why the member refused the call.
     * @param detail Which member refused, in words. Optional; it must not quote the refused value.
     */
    constructor(reason, detail) {
        super(detail === undefined ? reason : `${reason}: ${detail}`);
        this.reason = reason;
    }
}
// ── Runtime events (R-3) ──────────────────────────────────────────────────────────────────────
/** The six runtime event names (R-3, R-50, R-90). No surface output may carry the `surface:` prefix. */
export const SURFACE_RUNTIME_EVENTS = Object.freeze([
    'surface:input-rejected',
    'surface:output-rejected',
    'surface:load-failed',
    'surface:crashed',
    'surface:unmount-timeout',
    'surface:css-rejected',
]);
/** Where a surface crashed: its `mount`, `update` or `unmount`, or a render its framework reported through `reportError`. */
export const SURFACE_CRASH_PHASES = Object.freeze(['mount', 'update', 'unmount', 'render']);
/**
 * What the stylesheet scoper refused and dropped from an app stylesheet (R-90). The scoper keeps
 * only what it reads as safe, and names every other construct with one of these:
 *
 * - `at-rule`: an at-rule outside the kept set (`@scope`, `@import`, `@charset`, `@starting-style`,
 *   any unknown name), an at-rule name written with an escape, or a kept at-rule in a form the
 *   scoper does not keep (a `@media` with no block, a `@font-face` with a prelude, a `@keyframes`
 *   whose ident or string name is empty, `none` or a CSS-wide keyword). In an app stylesheet also (R-94,
 *   R-121): a `@property` whose name does not start with `--tw-` or whose registration differs from
 *   the registration of that name in force (the last one in document order), and a `@font-face` that
 *   holds a `{}` block or whose `font-family` names no family.
 * - `nested-rule`: a style rule or an at-rule inside the body of a style rule.
 * - `selector`: a style rule whose prelude is not a plain selector list: it holds `&`, a `{}` block,
 *   a `;`, an at-keyword or a url, a selector in it is empty or starts or ends with a combinator, or
 *   a bracket in it does not close.
 * - `declaration`: a part of a rule body that is not a well-formed declaration.
 * - `bad-string`: a string that a newline or the end of the stylesheet ended.
 * - `bad-url`: an unquoted `url(` that the browser reads as a bad url, or that the end of the
 *   stylesheet ended.
 * - `incomplete-rule`: a rule prelude that the end of the stylesheet cut before its block.
 */
export const CSS_REJECTED_CONSTRUCTS = Object.freeze([
    'at-rule',
    'nested-rule',
    'selector',
    'declaration',
    'bad-string',
    'bad-url',
    'incomplete-rule',
]);
//# sourceMappingURL=contract.js.map